import {
  ArrowDownwardRounded,
  CheckCircleOutlineRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  SxProps,
  useMediaQuery,
} from '@mui/material';
import { useRollbar } from '@rollbar/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { GTMService, rudderstackService, eventService } from '@services';
import { useStore } from '@stores';
import { globalTheme as theme } from '@styles';
import { RetryFailureError } from '@types';
import {
  compileEngagementInfo,
  handleEngagementStatus,
  sendEventToHost,
} from '@utils';

function isRetryFailureError(error: unknown): error is RetryFailureError {
  if (!(error instanceof Error)) {
    return false;
  }
  return (error as RetryFailureError).isRetryFailure === true;
}

interface FooterContinueBtnProps {
  onSubmit?: () => void;
  isDisabled?: boolean;
  callback?: () => void;
  buttonLabel?: string;
  sx?: SxProps;
  /**
   * Publishes this button's full submit handler so another surface can trigger
   * an identical submit. Used by the results-page win-back modal, whose primary
   * CTA must behave exactly like this button — including the engagement update,
   * analytics, module events and shared `buttonLoading` state, not just the
   * `callback`. Called with null on unmount.
   */
  registerSubmit?: (submit: (() => Promise<void>) | null) => void;
}

export const FooterContinueButton = observer(
  ({
    onSubmit,
    isDisabled,
    callback,
    buttonLabel,
    sx,
    registerSubmit,
  }: FooterContinueBtnProps) => {
    const { formStore, commonStore } = useStore();
    const methods = useFormContext();
    const { currentModuleName } = formStore;
    const { buttonLoading } = commonStore;
    const rollbar = useRollbar();
    const { t } = useTranslation('footer');
    const { t: tConsent } = useTranslation('subModules/contactInfo');
    const [showConsentPrompt, setShowConsentPrompt] = useState(false);
    const hasConsent = formStore.getSubmodule?.some(
      (name) => name === 'SUB_CONTACT' || name === 'SUB_COMPACT_CONTACT'
    );
    const agreementRead = methods?.watch('tcpaAgreement');
    const requiresTerms = hasConsent && !agreementRead;
    const { i18n } = useTranslation();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const continueButtonRef = useRef<HTMLButtonElement>(null);
    const [continueButtonWidth, setContinueButtonWidth] = useState<
      number | null
    >(null);

    const handleEngagement = async () => {
      if (methods?.getValues('email') && methods?.getValues('phone')) {
        const nextModuleName =
          formStore.formConfig.modules[formStore.currentIndex + 1]?.name;

        const gaClientId = await GTMService.getInstance().getGAClientId();
        eventService.updateEngagement(
          methods.getValues(),
          handleEngagementStatus(nextModuleName),
          {
            ...compileEngagementInfo(i18n),
            gaClientId,
          },
          methods.getValues('leadType')
        );
      }
    };

    const onClick = async () => {
      // Prevent double clicks
      if (commonStore.buttonLoading) {
        return;
      }

      if (requiresTerms) {
        setShowConsentPrompt(true);
        return;
      }
      if (isDisabled) {
        if (hasConsent)
          await methods?.trigger(undefined, { shouldFocus: true });
        return;
      }
      try {
        commonStore.setButtonLoading(true);
        // Revalidate before side effects; the loading guard also covers async validation.
        if (
          hasConsent &&
          (!methods ||
            !(await methods.trigger()) ||
            !methods.getValues('tcpaAgreement'))
        ) {
          commonStore.setButtonLoading(false);
          return;
        }

        rudderstackService.buttonClick(getButtonText());

        if (!formStore.hasApplicationStarted) {
          rudderstackService.applicationStarted();
          formStore.setApplicationStarted();
        }

        const hasSubContact = formStore.getSubmodule?.includes('SUB_CONTACT');
        if (hasSubContact) {
          rudderstackService.completedNameContactInfo();
        }

        await handleEngagement();

        if (onSubmit) {
          onSubmit();
        }

        try {
          await eventService.callEvents(formStore.currentModule?.events);
        } catch (error) {
          commonStore.setButtonLoading(false);
          if (isRetryFailureError(error)) {
            sendEventToHost('error', {
              data: {
                error: error as Error,
              },
            });
          }
          throw error;
        }

        if (callback) {
          callback();
        } else {
          setTimeout(() => {
            commonStore.setButtonLoading(false);
          }, 500);
          formStore.next();
        }
      } catch (error) {
        rollbar.error('An error occurred on submit', error as Error);
        sendEventToHost('error', {
          data: {
            error: error as Error,
          },
        });
        commonStore.setButtonLoading(false);
      }
    };

    // Keep a ref to the latest onClick so the registered submit is a stable
    // function that always runs the current closure.
    const onClickRef = useRef(onClick);
    useEffect(() => {
      onClickRef.current = onClick;
    });

    useEffect(() => {
      if (!registerSubmit) return;
      registerSubmit(() => onClickRef.current());
      return () => registerSubmit(null);
    }, [registerSubmit]);

    if (commonStore.showServicer) {
      return (
        <Button
          variant="contained"
          id={'servicerBtnId'}
          onClick={() => commonStore.setShowServicer(false)}
        >
          {t('servicerBtn')}
        </Button>
      );
    }

    const getButtonText = () => {
      if (buttonLabel) return buttonLabel;

      if (currentModuleName === 'MODULE_INTRO') {
        return t('introContinue');
      }

      let text = t('continue');
      const submodules = formStore.getSubmodule;

      const hasSubContact =
        submodules?.includes('SUB_CONTACT') ||
        submodules?.includes('SUB_COMPACT_CONTACT');

      const isPreviousModuleResultOptions =
        currentModuleName === formStore.previousModule('MODULE_RESULT_OPTIONS');

      if (hasSubContact) {
        text = t('contactContinue', {
          button: isPreviousModuleResultOptions
            ? t('seeOptions')
            : t('continue'),
        });
      } else if (isPreviousModuleResultOptions) {
        text = t('seeOptions');
      }

      return text;
    };

    const buttonText = getButtonText();
    const textLength = buttonText.length;

    const getPadding = () => {
      if (textLength > 30) {
        return { xs: '16px 16px', md: '18px 20px' };
      } else {
        return { xs: '18px 18px', md: '18px 28px' };
      }
    };

    if (
      continueButtonRef.current &&
      continueButtonWidth !== continueButtonRef.current.offsetWidth
    ) {
      setContinueButtonWidth(continueButtonRef.current?.offsetWidth);
    }

    return buttonLoading ? (
      <Button
        variant="contained"
        sx={{
          minWidth: `${continueButtonWidth}px`,
          height: isDesktop ? '3rem' : '3.5rem',
          minHeight: { md: '43.5px' },
          margin: '12px 0',
        }}
        disabled={!!buttonLoading || isDisabled}
        data-cy={currentModuleName}
      >
        <Box display="flex" alignItems="center" justifyContent="center">
          <CircularProgress
            sx={{ color: theme.palette.common.white }}
            size={24}
          />
        </Box>
      </Button>
    ) : (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: { xs: '100%', md: 'auto' },
            gap: hasConsent ? 1 : 0,
          }}
        >
          {hasConsent && (
            <Box
              id="consent-status"
              role="status"
              sx={{
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 0.75,
              }}
            >
              {requiresTerms ? (
                <ArrowDownwardRounded
                  sx={{ fontSize: 18, color: 'brandBlue.darker' }}
                />
              ) : (
                <CheckCircleOutlineRounded
                  sx={{ fontSize: 18, color: 'primary.main' }}
                />
              )}
              <Typography
                sx={{ fontSize: '12px', fontWeight: 600, lineHeight: '18px' }}
              >
                {tConsent(
                  requiresTerms
                    ? 'scrollToEnable'
                    : isDisabled
                      ? 'completeFields'
                      : 'readyToAgree'
                )}
              </Typography>
            </Box>
          )}
          <Button
            ref={continueButtonRef}
            variant="contained"
            sx={[
              {
                height:
                  hasConsent && !isDesktop
                    ? 'auto'
                    : isDesktop
                      ? '3rem'
                      : '3.5rem',
                minHeight: hasConsent && !isDesktop ? '48px' : undefined,
                fontWeight: 600,
                width: { md: 'auto' },
                textTransform: 'none',
                padding:
                  hasConsent && !isDesktop ? '10.5px 24px' : getPadding(),
                margin: hasConsent && !isDesktop ? 0 : '12px 0',
                borderRadius: hasConsent && !isDesktop ? '4px' : undefined,
                whiteSpace: hasConsent && !isDesktop ? 'normal' : 'nowrap',
                '&[aria-disabled=true]': {
                  backgroundColor: isDesktop
                    ? 'brandGray.light'
                    : 'brandGray.main',
                  color: 'brandGray.darker',
                  boxShadow: 'none',
                },
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            onClick={onClick}
            type="button"
            aria-disabled={
              hasConsent ? !!(isDisabled || requiresTerms) : undefined
            }
            aria-describedby={hasConsent ? 'consent-status' : undefined}
            disabled={!!buttonLoading || (!hasConsent && isDisabled)}
            data-cy={currentModuleName}
            id="CTAButton"
          >
            {buttonLoading ? (
              <CircularProgress
                style={{ color: theme.palette.text.secondary }}
                size={30}
              />
            ) : (
              buttonText
            )}
          </Button>
        </Box>
        <Dialog
          open={showConsentPrompt}
          onClose={() => setShowConsentPrompt(false)}
          aria-labelledby="consent-review-title"
          aria-describedby="consent-review-description"
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px' } }}
        >
          <DialogTitle id="consent-review-title" sx={{ fontWeight: 600 }}>
            {tConsent('consentPrompt.title')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="consent-review-description"
              sx={{ color: 'text.primary' }}
            >
              {tConsent('consentPrompt.description', {
                buttonLabel: buttonText,
                interpolation: { escapeValue: false },
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              type="button"
              variant="contained"
              onClick={() => setShowConsentPrompt(false)}
              sx={{ width: '100%', minHeight: 48, textTransform: 'none' }}
            >
              {tConsent('consentPrompt.continueReading')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);
